import type { Minor, Transaction, World } from './types'

/** Days an uncleared authorisation hold survives before it lapses. */
export const HOLD_EXPIRY_DAYS = 7

/** Days between a transaction clearing and the banks actually settling it. */
export const SETTLEMENT_LAG_DAYS = 1

export function openHolds(world: World): Transaction[] {
  return world.transactions.filter((t) => t.hold?.status === 'open')
}

/** Everything genuinely held, including approvals the issuer has not heard about. */
export function heldMinor(world: World): Minor {
  return openHolds(world).reduce((sum, t) => sum + (t.hold?.amountMinor ?? 0), 0)
}

/** Only the holds the issuer knows about. */
export function advisedHeldMinor(world: World): Minor {
  return openHolds(world)
    .filter((t) => t.advised)
    .reduce((sum, t) => sum + (t.hold?.amountMinor ?? 0), 0)
}

/** Approved in stand-in and not yet advised. The issuer is blind to this. */
export function unadvisedHeldMinor(world: World): Minor {
  return heldMinor(world) - advisedHeldMinor(world)
}

export function unadvisedTransactions(world: World): Transaction[] {
  return world.transactions.filter((t) => !t.advised && t.outcome !== 'declined')
}

function ceilingMinor(world: World): Minor {
  const { ledgerMinor, creditLimitMinor } = world.account
  return creditLimitMinor === undefined ? ledgerMinor : ledgerMinor + creditLimitMinor
}

/**
 * The balance the issuer believes it has, and therefore the one it authorises
 * against. While a stand-in approval is outstanding, this number is wrong.
 */
export function issuerAvailableMinor(world: World): Minor {
  return ceilingMinor(world) - advisedHeldMinor(world)
}

/** The true position, once every outstanding hold is counted. */
export function availableMinor(world: World): Minor {
  return ceilingMinor(world) - heldMinor(world)
}

/** Clearings that have posted but which the banks have not yet settled. */
export function dueForSettlement(world: World) {
  const rows: { transaction: Transaction; clearingId: string; amountMinor: Minor }[] = []
  for (const t of world.transactions) {
    for (const c of t.clearings) {
      if (!c.settled && world.day >= c.day + SETTLEMENT_LAG_DAYS) {
        rows.push({ transaction: t, clearingId: c.id, amountMinor: c.amountMinor })
      }
    }
  }
  return rows
}

/** Posted to the ledger but not yet settled between the banks. */
export function inFlightToMerchantMinor(world: World): Minor {
  let total = 0
  for (const t of world.transactions) {
    for (const c of t.clearings) if (!c.settled) total += c.amountMinor
  }
  return total
}

/** What the merchant genuinely banked, after everyone took their cut. */
export function merchantReceivedMinor(world: World): Minor {
  return world.settlements.reduce((sum, s) => sum + s.merchantNetMinor, 0)
}

/** What the cardholder was charged, in total, for settled transactions. */
export function settledGrossMinor(world: World): Minor {
  return world.settlements.reduce((sum, s) => sum + s.grossMinor, 0)
}

export function totalFeesMinor(world: World): Minor {
  return world.settlements.reduce(
    (sum, s) => sum + s.interchangeMinor + s.schemeFeeMinor + s.acquirerMarginMinor,
    0,
  )
}

export function cardById(world: World, cardId: string) {
  return world.cards.find((c) => c.id === cardId)
}

export function transactionById(world: World, id: string) {
  return world.transactions.find((t) => t.id === id)
}

export function tokensForCard(world: World, cardId: string) {
  return world.tokens.filter((t) => t.cardId === cardId)
}
